import {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';

import useFilters from '@/hooks/useFilters';
import {merchantTransactionService} from '@/services/merchants/transactionService';
import type {AfriexQueryParams, AfriexTransactionStatus} from '@/types';
import type {AfriexBusinessTransaction} from '@/types/AfriexBusinessTransaction';
import {formatAdminBusinessTransaction} from '@/utils/dashboard';
import {logRejection} from '@/utils/logRejection';

import {
  readFiltersFromUrl,
  toPanelFilters,
  writeFiltersToUrl,
} from './filtersInUrl';
import type {MerchantTransactionListProps} from './types';

// The Omit below drops three keys but keeps `handleApplyFilters`, so widening
// it on MerchantTransactionListProps covers this hook and the admin-tab one
// together.
const useMerchantBusinessTransactionList = (): Omit<
  MerchantTransactionListProps,
  'handleFetchTransactions' | 'transactionType' | 'setTransactionType'
> => {
  const {businessId} = useParams(); //can also be businessId
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status') as AfriexTransactionStatus;

  const [transactionList, setTransactionList] = useState<
    AfriexBusinessTransaction[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch] = useState<boolean>(false);
  const [transactionTotal, setTransactionTotal] = useState<number>(0);
  const [showPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  // Seeded from the URL, so Back from a transaction restores the filters.
  const [restoredFilters] = useState(() => readFiltersFromUrl(location.search));
  const [currentFilters, setCurrentFilters] =
    useState<AfriexQueryParams>(restoredFilters);
  const [exportFilters, setExportFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>(() =>
    toPanelFilters(restoredFilters),
  );
  const [filterCount, setFilterCount] = useState(
    () => Object.values(toPanelFilters(restoredFilters)).filter(Boolean).length,
  );

  useEffect(() => {
    writeFiltersToUrl(currentFilters);
  }, [currentFilters]);
  const {applyFilters} = useFilters(setCurrentFilters);

  // The `status` URL param is layered onto the filters the request is built
  // from rather than mirrored into `currentFilters` state by an effect. That
  // effect ran after the first render and always handed `currentFilters` a
  // fresh object identity, which recreated the fetch callback and fired a
  // second, identical request on every mount. `currentFilters` now holds only
  // the filters the admin picked, so its identity is stable and this memo is
  // stable with it.
  const effectiveFilters = useMemo(
    () => (status ? {...currentFilters, status} : currentFilters),
    [currentFilters, status],
  );

  const fetchUserTransactions = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const query = {...params, ...effectiveFilters, businessId};
        const transactionData =
          await merchantTransactionService.getBusinessTransactionList(query);
        const {page: _page, limit: _limit, ...filters} = query;
        setExportFilters(filters);

        const filtered = await Promise.all(
          transactionData?.data &&
            transactionData?.data.map(async (transaction) => {
              const at = await formatAdminBusinessTransaction(transaction);
              return {
                ...transaction,
                type: at.type,
                adminUser: at.adminUser,
              };
            }),
        );
        setTransactionList(filtered);
        setTransactionTotal(transactionData?.total || 0);
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [effectiveFilters],
  );

  // `MerchantTransactionListProps.fetchTransactionList` is
  // `(params) => void` (types.ts) and is handed to Pagination's
  // `onPageChange`, which is also `=> void`, so adapt at the boundary rather
  // than widening either contract. `fetchUserTransactions` catches into
  // `error` state, so this handler only fires if that catch or the finally
  // itself throws. `useCallback` keeps the identity stable.
  const fetchTransactionList = useCallback(
    (params?: AfriexQueryParams) => {
      fetchUserTransactions(params).catch(
        logRejection('Failed to load business transactions'),
      );
    },
    [fetchUserTransactions],
  );

  useEffect(() => {
    if (businessId && businessId?.trim() !== '') {
      fetchTransactionList({});
    }
  }, [fetchTransactionList]);

  const handleApplyFilters = useCallback(() => {
    // Layer the URL `status` onto a copy instead of mutating the state object.
    // `applyFilters` receives exactly the same filters as before; the
    // difference is that `filtersToApply` state is no longer written behind
    // React's back, so it stays owned by `selectAppliedFilters`.
    const applied = applyFilters(
      status ? {...filtersToApply, status} : filtersToApply,
    );
    // Pagination reset is success-only work. applyFilters returns false when it
    // rejects a date range, and Pagination reacts to this flag by calling
    // onPageChange -> a refetch, so toggling it unconditionally fired a request
    // the user never asked for. See hooks/useFilters.ts.
    if (!applied) {
      return false;
    }

    setIsPageReset((prev) => !prev);
    return true;
  }, [applyFilters, filtersToApply, status]);

  // Handle change for user manipulation
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  // Handle dates for applied filters
  const selectAppliedFilters = useCallback(
    (name: string, value: string | number | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Count only filters that are actually set. Counting `!== undefined`
        // kept an emptied text field (which holds '') in the tally, so Apply
        // stayed enabled and the label read `Apply Filters (1)` with nothing
        // filled in. This is the same emptiness rule `useFilters.applyFilters`
        // already applies one layer down, so the count and the applied set now
        // agree. No filter value is a meaningful 0.
        const filteredCount =
          Object.values(updatedFilters).filter(Boolean).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
    if (status) {
      setCurrentFilters({status});
    }
  };
  return {
    error,
    fetchTransactionList,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: transactionTotal,
    },
    showPagination,
    transactionList,
    exportFilters,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
  };
};

export default useMerchantBusinessTransactionList;
